import { useCurrentWell } from "@hooks/useCurrentWell";

/**
 * Resolve the pad/site for the current session (§12). A pad = a Site in the
 * Oxbo-M well register (wells belong to a site via `WellView.site`). The Oxbo-P
 * pad endpoints (`/v1/pads/{siteId}/…`) accept either the pad entity_id or the
 * pad_key/slug, and each well now carries its own `site` — so we resolve the
 * siteId straight from real data for whatever org the session belongs to:
 *
 *   1. the `site` of the currently-selected well, if one is selected;
 *   2. else the `site` of the first well in the register;
 *   3. else (no wells at all) a last-resort constant, so the mock/standalone
 *      build and an empty register still render instead of crashing.
 *
 * This keeps the demo org working (its wells carry their own pad `site`) while
 * no longer hardcoding any single org's pad.
 */
/**
 * Last-resort pad key, used only when the register returns no wells at all
 * (empty org, or a degraded M read). Any org with ≥1 well resolves from data.
 */
const FALLBACK_SITE_ID = "SITE-MRPYQNS7";

export function usePadSite(): { siteId: string | undefined; wellReady: boolean } {
  const { well, wells, isLoading } = useCurrentWell();

  // Prefer the selected well's site; else the first well's site.
  const resolved = well?.site ?? wells[0]?.site ?? null;

  // While wells are still loading we have no real siteId yet — hold the pad
  // query back (`wellReady: false`) rather than firing it with a placeholder.
  if (isLoading) {
    return { siteId: undefined, wellReady: false };
  }

  // Loaded: use the resolved site, or the constant only if the register is empty.
  const siteId = resolved ?? (wells.length === 0 ? FALLBACK_SITE_ID : undefined);
  return { siteId, wellReady: siteId != null };
}
